import { useEffect, useState } from 'react';

import { useQueryAssets } from '@/hooks/useAssets';
import { useGetRisks } from '@/hooks/useRisks';
import { AssetStatus, RiskStatus } from '@/types';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';

/**
 * Reusable risk-selection step shared by the Generate Report and Generate LOA
 * wizards. Lets the user choose which risks an export covers and resolves that
 * choice to a `risk_keys` array.
 *
 * Resolution semantics (`resolvedRiskKeys`):
 *  - `'all'`       → `undefined` (export covers ALL open risks; no keys sent).
 *  - `'specific'`  → the explicitly picked keys, or `undefined` when none picked.
 *  - `'dateRange'` → the keys of open risks whose chosen date field falls inside
 *                    `[startDate, endDate]`, resolved live via `useGetRisks`.
 *  - `'byAsset'`   → the keys of open risks linked to any of the selected assets
 *                    (via `HAS_VULNERABILITY|HAS_PORT|HAS_WEBPAGE`), resolved
 *                    live via `useGetRisks({ assetKeys })`; `undefined` until at
 *                    least one asset is picked.
 *
 * The hook instance is owned by the parent wizard (not the step component) so the
 * resolved keys are available at submit time.
 *
 * Pagination — IMPORTANT: `useGetRisks` is backed by `useInfiniteQuery` with a
 * 100-rows-per-page cap. Reading `query.data` alone would yield only the FIRST
 * page, silently capping the resolved scope (and the "specific" picker) at 100
 * risks — so a date-range/LOA export would omit every matching finding beyond
 * the first 100, and the picker could never reach the rest. To avoid that, this
 * hook auto-fetches every page to exhaustion (`fetchNextPage` while
 * `hasNextPage`) before exposing keys/options, and reports `isResolving` /
 * `isLoadingOptions` while pages are still loading so the wizards disable
 * Next/Generate until the COMPLETE set is loaded.
 */

export type RiskSelectionMode = 'all' | 'dateRange' | 'specific' | 'byAsset';

/** Which Risk date field the date-range filter compares against. */
export type RiskDateField = 'created' | 'visited';

/** A pickable open risk for the "specific" multi-select. */
export interface RiskOption {
  key: string;
  label: string;
  /** Full status code (e.g. `OC`), for StatusChip variant + label lookup. */
  status: string;
}

/** A pickable asset for the "byAsset" multi-select. */
export interface AssetOption {
  key: string;
  /** Human-readable display name (falls back to the identifier/DNS). */
  label: string;
  /** Secondary line — the asset identifier/DNS, shown muted under the label. */
  detail: string;
}

export interface RiskSelectionState {
  mode: RiskSelectionMode;
  setMode: (mode: RiskSelectionMode) => void;

  dateField: RiskDateField;
  setDateField: (field: RiskDateField) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;

  selectedKeys: string[];
  setSelectedKeys: (keys: string[]) => void;

  /** Asset keys picked in `'byAsset'` mode. */
  selectedAssetKeys: string[];
  setSelectedAssetKeys: (keys: string[]) => void;

  /**
   * Server-side search term for the `'byAsset'` asset picker. Drives
   * `useQueryAssets`'s fulltext search (key/name/dns/identifier) so the picker
   * never has to download the whole inventory — the user narrows by typing.
   */
  assetSearch: string;
  setAssetSearch: (search: string) => void;

  /**
   * The resolved `risk_keys` payload. `undefined` means "all open risks"
   * (mode `'all'`, an empty `'specific'` selection, a `'dateRange'` whose
   * bounds are not yet both set, or a `'byAsset'` with no asset picked yet).
   */
  resolvedRiskKeys: string[] | undefined;
  /**
   * True while the date-range OR by-asset query is fetching its matched keys
   * (including paging the full result set to exhaustion). The wizards gate
   * Next/Generate on this so the export never submits a capped/partial key set.
   */
  isResolving: boolean;
  /** Open risks available to pick in `'specific'` mode. */
  riskOptions: RiskOption[];
  /**
   * True while the `'specific'` picker is still loading its open-risk options
   * (including paging to exhaustion). Lets the picker distinguish "still
   * loading" from a genuinely empty result, so the empty state never lies.
   */
  isLoadingOptions: boolean;
  /** Pickable assets matching the current `assetSearch` (first page only). */
  assetOptions: AssetOption[];
  /**
   * True only while the asset picker's first page is loading. The picker does
   * NOT page to exhaustion (large inventories would stall it) — search narrows
   * the set server-side instead — so this never blocks on a full download.
   */
  isLoadingAssetOptions: boolean;
}

// Open risks only — the export wizards cover what's currently open. Mirrors the
// 'O' status code (RiskStatus.Opened) the risks table uses for "Open".
const OPEN_STATUS_FILTER = [RiskStatus.Opened];

// Assets worth scoping a report to — everything that's live (active passive,
// active low, active, frozen). Excludes Deleted/Pending/Unavailable so the
// picker only offers assets a finding could realistically be attributed to.
const PICKABLE_ASSET_STATUSES = [
  AssetStatus.ActivePassive,
  AssetStatus.ActiveLow,
  AssetStatus.Active,
  AssetStatus.Frozen,
];

export function useRiskSelection(): RiskSelectionState {
  const [mode, setMode] = useState<RiskSelectionMode>('all');
  const [dateField, setDateField] = useState<RiskDateField>('created');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedAssetKeys, setSelectedAssetKeys] = useState<string[]>([]);
  const [assetSearch, setAssetSearch] = useState('');

  const isDateRangeMode = mode === 'dateRange';
  const isSpecificMode = mode === 'specific';
  const isByAssetMode = mode === 'byAsset';
  const hasDateRange = !!startDate && !!endDate;
  const hasSelectedAssets = selectedAssetKeys.length > 0;

  // Date-range resolution. `useGetRisks` only honors `advancedFilters` (including
  // `dateRange`) when it runs in "drawer context" — i.e. `parentFilters` is set —
  // so the filter is carried via `parentFilters`, not `initialFilters` (which is
  // only the seed for the inert ephemeral-filter state). See useRisks.ts.
  const dateRangeQuery = useGetRisks({
    enabled: isDateRangeMode && hasDateRange,
    parentFilters: {
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
        dateRange: { field: dateField, startDate, endDate },
      },
    },
  });

  // Open-risk list for the "specific" multi-select picker.
  const specificQuery = useGetRisks({
    enabled: isSpecificMode,
    parentFilters: {
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
      },
    },
  });

  // Asset list for the "byAsset" multi-select picker. Search is carried via
  // `parentFilters.search` — that survives useQueryAssets's `{ ...parentFilters,
  // ...localFilters }` merge (localFilters has no `search` key), whereas a
  // `parentFilters.advancedFilters` would be clobbered by localFilters' empty
  // `advancedFilters: {}`. So the status restriction is applied client-side on
  // `assetOptions` below, and search is pushed to the server here.
  const assetQuery = useQueryAssets({
    enabled: isByAssetMode,
    parentFilters: { search: assetSearch },
  });

  // By-asset resolution: open risks linked to any of the selected assets. Scopes
  // via `assetKeys` (a single `key IN [...]` Asset source — see useRisks.ts), so
  // one query covers every selected asset rather than one query per asset.
  const byAssetQuery = useGetRisks({
    enabled: isByAssetMode && hasSelectedAssets,
    parentFilters: {
      assetKeys: selectedAssetKeys,
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
      },
    },
  });

  // Drive both infinite queries to exhaustion. `useGetRisks` pages 100 rows at a
  // time; without this, `.data` would only ever hold the first page. We fetch
  // the next page whenever one exists and we aren't already fetching — the
  // effect re-runs as each page lands (hasNextPage / isFetching change) until
  // `hasNextPage` is false, at which point `.data` holds the COMPLETE set.
  // Guarded on `hasNextPage` so there is no infinite loop.
  useEffect(() => {
    if (
      isDateRangeMode &&
      hasDateRange &&
      dateRangeQuery.hasNextPage &&
      !dateRangeQuery.isFetching
    ) {
      dateRangeQuery.fetchNextPage();
    }
  }, [
    isDateRangeMode,
    hasDateRange,
    dateRangeQuery.hasNextPage,
    dateRangeQuery.isFetching,
    dateRangeQuery.fetchNextPage,
  ]);

  useEffect(() => {
    if (
      isSpecificMode &&
      specificQuery.hasNextPage &&
      !specificQuery.isFetching
    ) {
      specificQuery.fetchNextPage();
    }
  }, [
    isSpecificMode,
    specificQuery.hasNextPage,
    specificQuery.isFetching,
    specificQuery.fetchNextPage,
  ]);

  // NOTE: the asset picker (assetQuery) is intentionally NOT paged to
  // exhaustion — on large tenants that would fire dozens of sequential
  // requests and stall the flow. Search narrows the set server-side instead.
  // The by-asset RISK resolution below IS exhausted, because `risk_keys` must
  // be the COMPLETE set or the export would silently drop findings.
  useEffect(() => {
    if (
      isByAssetMode &&
      hasSelectedAssets &&
      byAssetQuery.hasNextPage &&
      !byAssetQuery.isFetching
    ) {
      byAssetQuery.fetchNextPage();
    }
  }, [
    isByAssetMode,
    hasSelectedAssets,
    byAssetQuery.hasNextPage,
    byAssetQuery.isFetching,
    byAssetQuery.fetchNextPage,
  ]);

  const riskOptions: RiskOption[] = specificQuery.data.map(risk => ({
    key: risk.key,
    label: risk.title || risk.name,
    status: risk.status,
  }));

  // Restrict to pickable (live) statuses client-side — see the assetQuery note
  // above for why this can't go through parentFilters.advancedFilters.
  const assetOptions: AssetOption[] = assetQuery.data
    .filter(asset =>
      PICKABLE_ASSET_STATUSES.includes(asset.status as AssetStatus)
    )
    .map(asset => ({
      key: asset.key,
      label: asset.name || asset.identifier || asset.dns,
      detail: asset.identifier || asset.dns || '',
    }));

  const dateRangeKeys = dateRangeQuery.data.map(risk => risk.key);
  const byAssetKeys = byAssetQuery.data.map(risk => risk.key);

  let resolvedRiskKeys: string[] | undefined;
  if (mode === 'all') {
    resolvedRiskKeys = undefined;
  } else if (mode === 'specific') {
    resolvedRiskKeys = selectedKeys.length > 0 ? selectedKeys : undefined;
  } else if (mode === 'byAsset') {
    // 'byAsset': resolved keys only once at least one asset is picked; otherwise
    // treat as unconstrained (undefined = all open risks). `byAssetKeys`
    // reflects the COMPLETE paged result (see the exhaustion effect above).
    resolvedRiskKeys = hasSelectedAssets ? byAssetKeys : undefined;
  } else {
    // 'dateRange': resolved keys only once both bounds are set; otherwise treat
    // as unconstrained (undefined = all open risks). `dateRangeKeys` reflects
    // the COMPLETE paged result (see the exhaustion effect above).
    resolvedRiskKeys = hasDateRange ? dateRangeKeys : undefined;
  }

  // Resolving while the initial page is pending OR while more pages remain to be
  // fetched (hasNextPage). Either state means the resolved key set is not yet
  // complete, so the wizards must keep Next/Generate disabled. Covers both the
  // date-range and by-asset queries.
  const isResolving =
    (isDateRangeMode &&
      hasDateRange &&
      (dateRangeQuery.status === 'pending' || !!dateRangeQuery.hasNextPage)) ||
    (isByAssetMode &&
      hasSelectedAssets &&
      (byAssetQuery.status === 'pending' || !!byAssetQuery.hasNextPage));

  // Same exhaustion gate for the "specific" picker so its empty state never
  // shows "no risks" while pages are still loading.
  const isLoadingOptions =
    isSpecificMode &&
    (specificQuery.status === 'pending' || !!specificQuery.hasNextPage);

  // Only the first page matters for the picker (no exhaustion), so this reflects
  // just the initial/search-change load — never a full-inventory download.
  const isLoadingAssetOptions =
    isByAssetMode && assetQuery.status === 'pending';

  return {
    mode,
    setMode,
    dateField,
    setDateField,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedKeys,
    setSelectedKeys,
    selectedAssetKeys,
    setSelectedAssetKeys,
    assetSearch,
    setAssetSearch,
    resolvedRiskKeys,
    isResolving,
    riskOptions,
    isLoadingOptions,
    assetOptions,
    isLoadingAssetOptions,
  };
}

// Re-exported for callers that want the label for a risk status code.
export { getRiskStatusLabel };
